import { useState } from "react";
import { displaySize } from "../constants.js";
import "./SitePreviewDialog.css";

/** Rough ad-slot category from a size's dimensions - drives placement below. */
function slotOf(sizeId) {
  const s = displaySize(sizeId);
  if (!s) return "other";
  if (s.w >= 468 && s.h <= 120) return "leaderboard";
  if (s.w <= 336 && s.h <= 120) return "mobile";
  if (s.h >= 500) return "skyscraper";
  if (s.w >= 250 && s.w <= 340 && s.h >= 200 && s.h <= 300) return "rectangle";
  return "other";
}

function AdSlot({ sizeId, srcdocFor, previewKeyFor }) {
  const s = displaySize(sizeId);
  if (!s) return null;
  return (
    <div className="site-preview__ad-slot">
      <div className="site-preview__ad-label">Advertisement</div>
      <iframe
        key={previewKeyFor(sizeId)}
        title={`Site preview ${sizeId}`}
        sandbox="allow-same-origin"
        width={s.w}
        height={s.h}
        srcDoc={srcdocFor(sizeId)}
        style={{ width: s.w, height: s.h }}
        className="site-preview__ad-iframe"
      />
    </div>
  );
}

const bar = (w) => <div className="site-preview__bar" style={{ width: w }} />;
const para = (widths) => (
  <div className="site-preview__para">
    {widths.map((w, i) => (
      <div key={i} className="site-preview__bar" style={{ width: w }} />
    ))}
  </div>
);
const heading = (w) => <div className="site-preview__heading" style={{ width: w }} />;

/** Shows the banners embedded in a mock webpage, in realistic ad placements. */
export default function SitePreviewDialog({ sizes, srcdocFor, previewKeyFor, onClose }) {
  const [mobile, setMobile] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  const cat = { leaderboard: [], rectangle: [], skyscraper: [], mobile: [], other: [] };
  sizes.forEach((s) => cat[slotOf(s)].push(s));
  const placed = new Set();
  const take = (arr) => {
    const v = arr.find((x) => !placed.has(x));
    if (v) placed.add(v);
    return v;
  };
  const top = take(cat.leaderboard) ?? take(cat.mobile);
  const artRect = take(cat.rectangle);
  const sky = take(cat.skyscraper);
  const sideRect = take(cat.rectangle);
  const midAd = take(cat.other) ?? take(cat.leaderboard) ?? take(cat.mobile);
  const leftover = sizes.filter((s) => !placed.has(s));

  const slot = (sizeId) =>
    sizeId ? <AdSlot sizeId={sizeId} srcdocFor={srcdocFor} previewKeyFor={previewKeyFor} /> : null;

  return (
    <div
      className={`site-preview__overlay${fullscreen ? " site-preview__overlay--fullscreen" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Preview on a site"
    >
      <div className={`site-preview${fullscreen ? " site-preview--fullscreen" : ""}`}>
        <div className="site-preview__header">
          <h2 className="site-preview__title">Preview on a live page</h2>
          <div className="site-preview__header-actions">
            <div className="site-preview__device-toggle">
              {["Desktop", "Mobile"].map((d) => {
                const isMobile = d === "Mobile";
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setMobile(isMobile)}
                    aria-pressed={mobile === isMobile}
                    className={`site-preview__device-btn${mobile === isMobile ? " site-preview__device-btn--active" : ""}`}
                  >
                    {d}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => setFullscreen((f) => !f)}
              title={fullscreen ? "Exit fullscreen" : "Fullscreen"}
              aria-pressed={fullscreen}
              className={`site-preview__fullscreen-btn${fullscreen ? " site-preview__fullscreen-btn--active" : ""}`}
            >
              {fullscreen ? "⤢ Exit" : "⛶ Fullscreen"}
            </button>
            <button type="button" onClick={onClose} aria-label="Close" className="site-preview__close">
              ✕
            </button>
          </div>
        </div>

        {/* The mock website - deliberately light + neutral so the banners stand out. */}
        <div className="site-preview__scroll">
          <div
            className={`site-preview__page${mobile ? " site-preview__page--mobile" : ""}${
              fullscreen ? " site-preview__page--fullscreen" : ""
            }`}
          >
            {/* nav - sticky like a real site header */}
            <div className="site-preview__nav">
              <div className="site-preview__nav-logo" />
              {!mobile ? (
                <div className="site-preview__nav-links">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className="site-preview__nav-link" />
                  ))}
                </div>
              ) : null}
            </div>

            <div className="site-preview__content">
              {top ? <div className="site-preview__top-ad">{slot(top)}</div> : null}

              <div className={`site-preview__layout${mobile ? " site-preview__layout--mobile" : ""}`}>
                {/* article */}
                <div className="site-preview__article">
                  <div className={`site-preview__hero${mobile ? " site-preview__hero--mobile" : ""}`} />
                  {heading("85%")}
                  {heading("55%")}
                  {para(["100%", "97%", "92%", "99%"])}
                  {artRect ? <div className="site-preview__inline-ad">{slot(artRect)}</div> : null}
                  {para(["100%", "95%", "88%", "99%", "72%"])}
                  {heading("48%")}
                  {para(["100%", "93%", "98%", "84%"])}
                  {midAd ? <div className="site-preview__inline-ad">{slot(midAd)}</div> : null}
                  {para(["100%", "96%", "90%", "99%", "78%", "94%"])}
                  {heading("40%")}
                  {para(["100%", "88%", "97%", "82%"])}
                </div>

                {/* sidebar - sticky inner wrapper so the skyscraper stays in view */}
                {!mobile && (sky || sideRect) ? (
                  <div className="site-preview__sidebar-col">
                    <div className="site-preview__sidebar-sticky">
                      {slot(sky)}
                      {slot(sideRect)}
                      <div className="site-preview__related-list">
                        {[0, 1, 2, 3].map((i) => (
                          <div key={i} className="site-preview__related-item">
                            <div className="site-preview__related-thumb" />
                            <div className="site-preview__related-text">
                              {bar("100%")}
                              {bar("70%")}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>

              {/* related cards row - more scroll height */}
              <div className="site-preview__related-section">
                {heading("30%")}
                <div className={`site-preview__related-grid${mobile ? " site-preview__related-grid--mobile" : ""}`}>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <div key={i}>
                      <div className="site-preview__related-card-img" />
                      {bar("100%")}
                      {bar("60%")}
                    </div>
                  ))}
                </div>
              </div>

              {/* any remaining formats */}
              {leftover.length > 0 ? (
                <div className="site-preview__leftover">
                  <div className="site-preview__leftover-label">Other placements</div>
                  <div className="site-preview__leftover-grid">
                    {leftover.map((s) => (
                      <div key={s}>{slot(s)}</div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            {/* footer */}
            <div className={`site-preview__footer${mobile ? " site-preview__footer--mobile" : ""}`}>
              {[0, 1, 2, 3].map((c) => (
                <div key={c}>
                  <div className="site-preview__footer-heading" />
                  {[0, 1, 2].map((r) => (
                    <div key={r} className="site-preview__footer-line" style={{ width: `${80 - r * 12}%` }} />
                  ))}
                </div>
              ))}
            </div>
          </div>
          <p className="site-preview__caption">
            A mock page — the banners are the live preview, placed in typical ad slots. Scroll to see them in
            context.
          </p>
        </div>
      </div>
    </div>
  );
}